import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, Mail, Megaphone, MapPin } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { submitContact } from "../lib/api";
import { trackFormAction } from "../lib/tracker";
import useRevealWhen from "../hooks/useRevealWhen";
import { MARKETING_EMAIL, SOCIAL_LINKS } from "../content/social";

// Topic chips, in display order. The general "familyMeals" label stays in the copy for older ?role links.
const PLATFORM_KEYS = [
  "heartOfHouse",
  "familyMealsSupplier",
  "familyMealsDistributor",
  "atHome",
  "partnership",
  "other",
];

const ROLE_TO_PLATFORM = {
  "family-meals-supplier": "familyMealsSupplier",
  "family-meals-distributor": "familyMealsDistributor",
  "heart-of-house": "heartOfHouse",
  "at-home": "atHome",
  partnership: "partnership",
};

// Brand glyphs at 24x24, filled with currentColor (same marks as SocialLinks).
const SOCIAL_ICONS = {
  instagram:
    "M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 4.9a4.9 4.9 0 110 9.8 4.9 4.9 0 010-9.8zm0 8.1a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zm5.1-8.3a1.1 1.1 0 110-2.3 1.1 1.1 0 010 2.3z",
  tiktok:
    "M16.6 2h-3.3v13.4a2.9 2.9 0 11-2.9-2.9c.3 0 .6 0 .8.1V9.2a6.3 6.3 0 00-.8-.1 6.2 6.2 0 106.2 6.2V8.6a8 8 0 004.6 1.5V6.8a4.6 4.6 0 01-4.6-4.6z",
  facebook:
    "M13.5 21.9v-7.7h2.6l.4-3h-3V9.3c0-.9.3-1.5 1.5-1.5h1.6V5.1c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4v2.2H7.8v3h2.6v7.7C5.6 21.2 2 17 2 12 2 6.5 6.5 2 12 2s10 4.5 10 10c0 5-3.6 9.2-8.5 9.9z",
  linkedin:
    "M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z",
};

const initialForm = {
  name: "",
  email: "",
  restaurant_name: "",
  city: "",
  platform_of_interest: "heartOfHouse",
  message: "",
};

const inputClass =
  "w-full min-w-0 bg-[#121212] border border-[#3a3a3a] focus:border-[#E8490F] focus:ring-[3px] focus:ring-[#E8490F]/20 outline-none rounded-xl px-3.5 py-2.5 text-[15px] text-white placeholder:text-white/30 transition-colors";

const Contact = () => {
  const { t } = useLanguage();
  const p = t.contact;
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRevealWhen(submitted);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [p.metaTitle]);

  // Pre-select the topic when a ?role= query string is present.
  useEffect(() => {
    const role = searchParams.get("role");
    if (role && ROLE_TO_PLATFORM[role]) {
      setForm((f) => ({ ...f, platform_of_interest: ROLE_TO_PLATFORM[role] }));
    }
  }, [searchParams]);

  const setField = (name, value) => {
    if (form.name === "" && form.email === "" && form.message === "") {
      trackFormAction("contact:start");
    }
    setForm((f) => ({ ...f, [name]: value }));
  };
  const onChange = (e) => setField(e.target.name, e.target.value);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const platformLabel = p.form.platforms[form.platform_of_interest] || form.platform_of_interest;
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        restaurant_name: form.restaurant_name.trim() || null,
        city: form.city.trim() || null,
        platform_of_interest: platformLabel,
        message: form.message.trim(),
      });
      trackFormAction("contact:submit");
      setSubmitted(true);
      toast.success(p.form.success);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error(p.form.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const direct = [
    { icon: Mail, label: p.supportLabel, value: p.directEmail, href: `mailto:${p.directEmail}`, testId: "contact-direct-email" },
    { icon: Megaphone, label: p.marketingLabel, value: MARKETING_EMAIL, href: `mailto:${MARKETING_EMAIL}`, testId: "contact-marketing-email" },
    { icon: MapPin, label: p.hqLabel, value: p.hq },
  ];

  return (
    <div data-testid="page-contact">
      {/* One screen on desktop: words and direct lines left, the form right */}
      <section className="relative overflow-hidden lg:min-h-[calc(100vh-4.5rem)] lg:flex lg:items-center">
        <div
          className="pointer-events-none absolute -left-52 -top-60 h-[620px] w-[620px] rounded-full bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-56 -right-32 h-[460px] w-[460px] rounded-full bg-[#E8490F]/10 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative w-full py-10 md:py-12">
          <div className="mx-auto max-w-5xl grid gap-10 lg:grid-cols-[4.3fr_7.7fr] lg:gap-14 items-center">
            <div className="min-w-0">
              <p className="mise-eyebrow">{p.eyebrow}</p>
              <h1 className="mt-3 text-5xl md:text-6xl lg:text-[4.6rem] font-semibold tracking-tight leading-[0.95]">
                {p.heading}
                <span className="mt-2 block mise-serif mise-accent text-[0.78em] leading-[1.05]">{p.headingItalic}</span>
              </h1>
              <p className="mt-5 max-w-sm text-base text-white/70 leading-relaxed">{p.sub}</p>

              <ul className="mt-6" data-testid="contact-direct">
                {direct.map(({ icon: Icon, label, value, href, testId }) => {
                  const body = (
                    <>
                      <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8490F]/15 text-[#E8490F]">
                        <Icon size={15} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[11.5px] text-white/40">{label}</span>
                        <span className="block truncate text-[15px] font-medium text-white">{value}</span>
                      </span>
                    </>
                  );
                  return (
                    <li key={label} className="border-t border-[#2a2a2a] first:border-t-0">
                      {href ? (
                        <a href={href} className="flex items-center gap-3 py-2.5 transition-colors hover:text-[#E8490F]" data-testid={testId} data-track={testId}>
                          {body}
                        </a>
                      ) : (
                        <div className="flex items-center gap-3 py-2.5">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 flex gap-2" data-testid="contact-social">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Mise on ${s.label}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#3a3a3a] text-white/80 transition-colors hover:border-[#E8490F] hover:text-[#E8490F]"
                    data-testid={`contact-social-${s.key}`}
                    data-track={`contact-social-${s.key}`}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                      <path d={SOCIAL_ICONS[s.key]} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* the form card, with a glowing orange edge */}
            <div className="min-w-0 rounded-[22px] bg-gradient-to-br from-[#E8490F]/75 via-[#444444]/50 to-[#242424] p-px shadow-[0_40px_80px_-40px_rgba(232,73,15,0.45)]">
              {submitted ? (
                <div
                  className="rounded-[21px] bg-[#1a1a1a] p-10 text-center outline-none"
                  data-testid="contact-success"
                  ref={successRef}
                  tabIndex={-1}
                >
                  <CheckCircle2 className="mx-auto text-[#E8490F]" size={42} />
                  <h3 className="mt-5 text-2xl font-semibold text-white">{p.form.success}</h3>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 mise-pill-secondary"
                    data-testid="contact-reset"
                  >
                    ↺
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-3.5 rounded-[21px] bg-[#1a1a1a] p-6 md:p-7" data-testid="contact-form">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label={p.form.name} name="name" value={form.name} onChange={onChange}
                      placeholder={p.form.placeholders.name} required testId="contact-name" />
                    <Field label={p.form.email} type="email" name="email" value={form.email} onChange={onChange}
                      placeholder={p.form.placeholders.email} required testId="contact-email" />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label={p.form.restaurant} name="restaurant_name" value={form.restaurant_name} onChange={onChange}
                      placeholder={p.form.placeholders.restaurant} testId="contact-restaurant" />
                    <Field label={p.form.city} name="city" value={form.city} onChange={onChange}
                      placeholder={p.form.placeholders.city} testId="contact-city" />
                  </div>

                  <fieldset className="grid gap-1.5">
                    <legend className="mb-1.5 text-[12.5px] text-white/50">{p.form.platform}</legend>
                    <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid="contact-platform">
                      {PLATFORM_KEYS.map((k) => {
                        const on = form.platform_of_interest === k;
                        return (
                          <button
                            key={k}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => setField("platform_of_interest", k)}
                            className={`rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                              on
                                ? "border-[#E8490F] bg-[#E8490F]/15 text-white"
                                : "border-[#353535] bg-[#151515] text-white/70 hover:border-[#666666] hover:text-white"
                            }`}
                            data-testid={`contact-platform-${k}`}
                          >
                            {p.form.platforms[k]}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div className="grid gap-1.5">
                    <label className="text-[12.5px] text-white/50" htmlFor="message">
                      {p.form.message}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      required
                      placeholder={p.form.placeholders.message}
                      className={`${inputClass} resize-none leading-relaxed`}
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="mise-pill-primary shadow-[0_12px_30px_-12px_rgba(232,73,15,0.8)] disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="contact-submit"
                    >
                      {submitting ? p.form.sending : p.form.submit}
                      {!submitting && <Send size={16} />}
                    </button>
                    <span className="text-[12.5px] text-white/40">{p.replyNote}</span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, required, testId }) => (
  <div className="grid gap-1.5 min-w-0">
    <label className="text-[12.5px] text-white/50" htmlFor={name}>
      {label}
      {required && <span className="text-[#E8490F]"> *</span>}
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className={inputClass}
      data-testid={testId}
    />
  </div>
);

export default Contact;
